import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import {
  AcceptanceDocumentVm,
  ChangePackageSettlementVm,
  ChangeRoute,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { SettlementDistribution } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import { Card } from '@/components/ui/Card.tsx';
import { ScreenTitle } from '@/components/ui/Title.tsx';
import {
  AcceptanceDocumentList,
  ChangePackageStepBadge,
  ChangeRouteChoice,
  CorrectionTermsFields,
  SettlementOptionList,
  SettlementSchedulePanel,
} from '@/features/dashboard/bookingChanges/components/flow';
import {
  packageBalanceContextLabel,
  settlementCtaLabel,
  settlementFootnote,
  settlementScopeSentence,
} from '@/features/dashboard/bookingChanges/components/flow/changePackageCopy.ts';
import { areDocumentsRelevant } from '@/features/dashboard/bookingChanges/components/flow/settlementChoice.ts';
import { settlementStepGuard } from '@/features/dashboard/bookingChanges/components/flow/changePackageSteps.ts';
import { isZeroMoneyList } from '@/features/dashboard/bookingChanges/utils/money.ts';

interface ChangePackageSettlementStepProps
  extends React.HTMLAttributes<HTMLDivElement> {
  settlement: ChangePackageSettlementVm;
  option: SettlementDistribution;
  onOptionChange: (option: SettlementDistribution) => void;
  route: ChangeRoute;
  onRouteChange: (route: ChangeRoute) => void;
  selectedDocumentIds: string[];
  onToggleDocument: (document: AcceptanceDocumentVm) => void;
  correctionDueDate: string | null;
  onCorrectionDueDateChange: (dueDate: string | null) => void;
  correctionPaymentMethods: PaymentMethod[];
  onToggleCorrectionPaymentMethod: (method: PaymentMethod) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

/**
 * Krok W2 — rozliczenie różnicy: rozkład dopłaty, warunki płatności korekty,
 * trasa wprowadzenia i dokumenty do ponownej akceptacji.
 *
 * Cały stan wyboru siedzi na stronie przepływu, tutaj przychodzi propsami —
 * dzięki temu stopka wie, którą trasą pójdzie paczka, a warunki korekty jadą
 * do `resolve` prosto z tego, co widzi operator.
 */
const ChangePackageSettlementStep = ({
  settlement,
  option,
  onOptionChange,
  route,
  onRouteChange,
  selectedDocumentIds,
  onToggleDocument,
  correctionDueDate,
  onCorrectionDueDateChange,
  correctionPaymentMethods,
  onToggleCorrectionPaymentMethod,
  onBack,
  onSubmit,
  isSubmitting,
  className,
  ...props
}: ChangePackageSettlementStepProps) => {
  const guard = settlementStepGuard({
    settlement,
    correctionDueDate,
    correctionPaymentMethods,
    isSubmitting,
  });
  const withBalance = !isZeroMoneyList(settlement.balance);

  return (
    <Card
      highlighted
      className={cn('@container overflow-hidden', className)}
      {...props}
    >
      <div className="@min-[700px]:px-7 border-b border-neutral-100 px-4 pb-5 pt-[22px]">
        <ChangePackageStepBadge
          step={ChangePackageStep.SETTLEMENT}
          context={packageBalanceContextLabel(
            settlement.packageNumber,
            settlement.balance,
          )}
        />
        <ScreenTitle className="mt-2.5">Rozliczenie różnicy</ScreenTitle>
        <p className="mt-1.5 max-w-[620px] text-pretty text-[15px] text-neutral-600">
          {settlementScopeSentence(settlement)}
        </p>
      </div>

      {/* Panel harmonogramu schodzi pod listę wariantów dopiero, gdy obok
          zabraknie dla niego miejsca — nie wcześniej. */}
      <div className="@max-[860px]:flex-col flex items-stretch">
        <div className="@min-[700px]:px-7 min-w-0 flex-1 px-4 pb-[22px] pt-[18px]">
          {/* Bez skutku finansowego nie ma czego rozkładać ani kiedy dopłacić —
              zostaje sam harmonogram obok, żeby było widać, że stoi w miejscu. */}
          {withBalance && (
            <SettlementOptionList
              options={settlement.options}
              value={option}
              onChange={onOptionChange}
            />
          )}
          {withBalance && (
            <CorrectionTermsFields
              terms={settlement.correctionTerms}
              dueDate={correctionDueDate}
              onDueDateChange={onCorrectionDueDateChange}
              paymentMethods={correctionPaymentMethods}
              onTogglePaymentMethod={onToggleCorrectionPaymentMethod}
            />
          )}
        </div>
        <SettlementSchedulePanel schedule={settlement.schedule} />
      </div>

      <div className="@min-[700px]:px-7 border-t border-neutral-200 px-4 pb-[22px] pt-6">
        <ChangeRouteChoice
          routes={settlement.routes}
          value={route}
          onChange={onRouteChange}
        />

        {areDocumentsRelevant(route) && (
          <AcceptanceDocumentList
            className="mt-[18px]"
            documents={settlement.documents}
            selectedIds={selectedDocumentIds}
            onToggle={onToggleDocument}
          />
        )}
      </div>

      <div className="@max-[700px]:flex-col @max-[700px]:items-stretch @min-[700px]:px-7 flex items-center gap-3.5 border-t border-neutral-100 bg-basic-white px-4 pb-[22px] pt-[18px]">
        <span className="min-w-0 max-w-[520px] text-pretty text-sm text-neutral-600">
          {settlementFootnote(route)}
        </span>
        <div className="@max-[700px]:ml-0 @max-[700px]:flex-col ml-auto flex shrink-0 gap-2.5">
          <ActionButton tone="outline" onClick={onBack}>
            Wstecz
          </ActionButton>
          <ActionButton
            tone={route === ChangeRoute.CLIENT_ACCEPTANCE ? 'accent' : 'dark'}
            onClick={onSubmit}
            disabled={!guard.canProceed}
            title={guard.reason}
          >
            {settlementCtaLabel(route)}
            {route === ChangeRoute.CLIENT_ACCEPTANCE && (
              <ArrowRight aria-hidden />
            )}
          </ActionButton>
        </div>
      </div>
    </Card>
  );
};

export default ChangePackageSettlementStep;
